import type { FieldPart } from "@/types/field-observations.types";
import $ls, { LocalStorageKey } from "@/utils/local-storage";

/**
 * Numérotation hiérarchique des parties d'ouvrage : « 1 », « 1.1 », « 1.2.3 »…
 *
 * Calculée sur l'arbre COMPLET, dans l'ordre d'affichage renvoyé par l'API. Une
 * vue qui masque des branches (l'arbre des observations ne montre que celles qui
 * portent des observations) garde ainsi les mêmes numéros que la modal d'édition
 * — sans quoi une partie sans observation décalerait toute la numérotation.
 *
 * @returns id de partie → chemin numéroté.
 */
export const buildPartNumbering = (
  parts: FieldPart[] | undefined
): Map<number, string> => {
  const numbering = new Map<number, string>();
  if (!parts?.length) return numbering;

  const byParent = new Map<number | null, FieldPart[]>();
  for (const part of parts) {
    const key = part.parentId ?? null;
    const siblings = byParent.get(key);
    if (siblings) siblings.push(part);
    else byParent.set(key, [part]);
  }

  const walk = (parentId: number | null, prefix: string) => {
    const siblings = byParent.get(parentId) ?? [];
    siblings.forEach((part, index) => {
      const path = prefix ? `${prefix}.${index + 1}` : `${index + 1}`;
      numbering.set(part.id, path);
      walk(part.id, path);
    });
  };
  walk(null, "");

  return numbering;
};

/**
 * Dernière partie d'ouvrage choisie sur cet ouvrage (mémorisée par
 * navigateur) — proposée par défaut à la création d'une observation.
 * undefined si rien n'est mémorisé ou si la valeur est inexploitable : une
 * valeur non numérique donnerait NaN, sérialisé en `null` dans le JSON de
 * création, que le schéma du back rejette (400).
 */
export function readLastSelectedFieldPart(
  diagnosticId: number,
): number | undefined {
  const stored = $ls.getItem(LocalStorageKey.LAST_SELECTED_FIELD_PART);
  if (!stored) return undefined;
  try {
    const id = Number(JSON.parse(stored)[diagnosticId]);
    return Number.isFinite(id) && id > 0 ? id : undefined;
  } catch {
    return undefined;
  }
}
